import { FunctionComponent, useState } from "react"
import { FormI } from "./types"
import ModalFooter from "../shared/modalFooter"
import Alert from "../shared/alert"
import Form from "./form"
import { useOrganizationMembershipAccessForm } from "./hook"
import {
    getUsersUpdateOrganizationMembershipInput,
    getWellPermissionsSetInput,
    isSubmitFormDisabled,
} from "./api"
import { SubjectRole } from "../../generated/graphql"
import { useMutation } from "@apollo/client"
import { WELL_PERMISSIONS_SET } from "../../graphql/mutations/well_permissions_set"
import { USERS_UPDATE_ORG_MEMBERSHIP } from "../../graphql/mutations/user_organization_membership_update"
import { useNavigate } from "react-router-dom"

interface OrganizationMembershipAccessUpdateFormContainerProps {
    initialForm: FormI
    targetSubjectName: string
    targetSubjectRole: SubjectRole
}

const FormContainer: FunctionComponent<
    OrganizationMembershipAccessUpdateFormContainerProps
> = ({ initialForm, targetSubjectName, targetSubjectRole }) => {
    // hooks
    const navigate = useNavigate()

    // state
    const {
        form,
        handleManagedAccessChange,
        handleWellPermissionsChange,
        handleSelectAllWells,
    } = useOrganizationMembershipAccessForm(initialForm)
    const [errorBanner, setErrorBanner] = useState({
        error: false,
        message: "",
    })

    // mutation
    const [wellPermissionsSet, { loading: wellPermissionsSetLoading }] =
        useMutation(WELL_PERMISSIONS_SET)

    const [
        organizationMembershipUpdate,
        { loading: organizationMembershipUpdateLoading },
    ] = useMutation(USERS_UPDATE_ORG_MEMBERSHIP)

    // handelSubmit
    // - Update organization membership if form.fullAccess is changed
    // - if form.fullAccess is false, set well permissions
    const handleSubmit = async () => {
        // No form validation or sanitization neccessary
        // Step 1: Update organization membership access
        if (initialForm.fullAccess !== form.fullAccess) {
            try {
                await organizationMembershipUpdate({
                    variables: {
                        UsersUpdateOrganizationMembershipInput:
                            getUsersUpdateOrganizationMembershipInput(form),
                    },
                })
            } catch (error) {
                setErrorBanner({
                    error: true,
                    message: `There was a problem completing the request. ${targetSubjectName}'s access status and well permissions didn't update. Please reopen the modal and complete the process again.`,
                })
                console.error(error)
                return
            }
        }
        // Step 2: Set well permissions if fullAccess was changed to false
        if (!form.fullAccess) {
            try {
                await wellPermissionsSet({
                    variables: {
                        WellPermissionsSetInput:
                            getWellPermissionsSetInput(form),
                    },
                })
            } catch (error) {
                setErrorBanner({
                    error: true,
                    message: `There was a problem completing the request. ${targetSubjectName} well permissions didn't update. Please reopen the modal and complete the process again.`,
                })
                console.error(error)
                return
            }
        }
        // Step 3: Navigate back to users admin table if no errors
        navigate(-1)
    }

    const isLoading =
        wellPermissionsSetLoading || organizationMembershipUpdateLoading

    // constants
    const disableSubmit = isSubmitFormDisabled(
        initialForm,
        form,
        errorBanner.error,
        isLoading
    )

    return (
        <>
            <Form
                form={form}
                targetSubjectName={targetSubjectName}
                targetSubjectRole={targetSubjectRole}
                handleManagedAccessChange={handleManagedAccessChange}
                handleWellPermissionsChange={handleWellPermissionsChange}
                handleSelectAllWells={handleSelectAllWells}
            />
            {errorBanner.error && (
                <Alert
                    message={errorBanner.message}
                    alertType="danger"
                    isCloseable={false}
                />
            )}
            <ModalFooter
                advanceText="Submit"
                handleAdvanceClick={handleSubmit}
                disableAdvance={disableSubmit}
            />
        </>
    )
}

export default FormContainer
